import React, { ReactElement, ReactNode } from 'react';

import { createMarkup } from 'app/utils';
import styled, { ColorsTypes, FontsTypes, ThemesItems } from 'styled-components';

interface RichContentProps {
  children: ReactNode;
  currentTheme?: ThemesItems;
  defaultType?: FontsTypes;
  textColor?: ColorsTypes;
  linkColor?: ColorsTypes;
}

const Wrapper = styled('div')`
  ${(props: any) => props.theme.fontTypes[props['data-defaulttype']]}

  color: ${(props: any) =>
    props['data-color']
      ? props.theme.colors[props['data-color']]
      : props['data-theme']
      ? props['data-theme'] === 'light'
        ? props.theme.colors.black80
        : props.theme.colors.white80
      : props.theme.colors.dark_350};

  word-break: break-word;

  a {
    color: ${(props: any) => props.theme.colors[props['data-linkcolor']]};

    &:hover {
      text-decoration: underline;
    }
  }

  p {
    margin-top: 14px;

    &:first-child {
      margin-top: 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  h1 {
    ${({ theme }) => theme.fontTypes.type11}
    line-height: 1.2;
  }

  h2 {
    ${({ theme }) => theme.fontTypes.type8}
    line-height: 1.2;
  }

  h3 {
    ${({ theme }) => theme.fontTypes.type5}
    line-height: 1.2;
  }

  h4 {
    ${({ theme }) => theme.fontTypes.type3}
    line-height: 1.2;
  }

  .mt5 {
    margin-top: 5px;
  }
  .mt10 {
    margin-top: 10px;
  }
  .mt15 {
    margin-top: 15px;
  }
  .mt20 {
    margin-top: 20px;
  }
  .mt30 {
    margin-top: 30px;
  }

  .upper {
    text-transform: uppercase;
  }

  .content-table {
    display: flex;
    flex-wrap: wrap;

    .cell-right {
      margin-top: 30px;
    }

    ${({ theme: { mediaQueries } }) => {
      return `
        ${mediaQueries.desktop} {
          flex-wrap: nowrap;

          .cell-left,
          .cell-right {
            width: 50%;
          }

          .cell-left {
            padding-right: 30px;
          }

          .cell-right {
            margin-top: 0;
            padding-left: 30px;
          }
        }
      `;
    }}
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      padding-left: 15px;
      position: relative;
      padding-bottom: 5px;

      &:last-child {
        padding-bottom: 0;
      }

      &::before {
        content: '';
        display: block;
        width: 8px;
        height: 8px;
        background: ${({ theme }) => theme.colors.primary_400};
        position: absolute;
        top: 8px;
        left: 0px;
      }
    }
  }

  img[src$='.svg'] {
    ${(props: any) => {
      if (props['data-theme'] === 'dark') {
        return `
          filter: invert(1);
        `;
      }
    }}
  }
`;

const RichContent = ({ children, defaultType = 'type3', currentTheme, textColor, linkColor = 'primary_400' }: RichContentProps): ReactElement => {
  return (
    <Wrapper
      data-color={textColor}
      data-linkcolor={linkColor}
      data-defaulttype={defaultType}
      data-theme={currentTheme}
      dangerouslySetInnerHTML={createMarkup(children)}
    />
  );
};

export default RichContent;
